/**
 * Voz e vídeo (WebRTC).
 *
 * O servidor NÃO transporta mídia: ele só faz o papel de "correio" da
 * sinalização (offer/answer/candidates) entre jogadores da mesma sala. O áudio e
 * o vídeo andam direto entre os navegadores (malha P2P).
 *
 * A única regra que precisa ser combinada entre as partes é QUEM inicia a
 * oferta. Se os dois iniciarem, as duas conexões brigam; se nenhum iniciar, nada
 * acontece. A regra adotada é determinística: inicia quem tem o menor id — assim
 * os dois lados chegam à mesma conclusão sem combinar nada antes.
 */
import { z } from 'zod';

export const RtcMediaSchema = z.object({
  /** está na conferência (mesmo com microfone e câmera desligados) */
  joined: z.boolean().default(false),
  /** microfone ligado */
  audio: z.boolean(),
  /** câmera ligada */
  video: z.boolean(),
  /** compartilhando a tela */
  screen: z.boolean().default(false),
});
export type RtcMedia = z.infer<typeof RtcMediaSchema>;

export const RTC_SIGNAL_KINDS = ['offer', 'answer', 'candidate'] as const;
export type RtcSignalKind = (typeof RTC_SIGNAL_KINDS)[number];

export const RtcIceCandidateSchema = z.object({
  candidate: z.string().max(4000),
  sdpMid: z.string().max(64).nullable(),
  sdpMLineIndex: z.number().int().nullable(),
  usernameFragment: z.string().max(256).nullable().optional(),
});
export type RtcIceCandidate = z.infer<typeof RtcIceCandidateSchema>;

export const RtcSignalSchema = z
  .object({
    kind: z.enum(RTC_SIGNAL_KINDS),
    /** SDP da oferta/resposta */
    sdp: z.string().max(200_000).nullable().optional(),
    /** candidato ICE */
    candidate: RtcIceCandidateSchema.nullable().optional(),
  })
  .refine((signal) => (signal.kind === 'candidate' ? signal.candidate != null : signal.sdp != null), {
    message: 'candidate exige candidate; offer/answer exigem sdp',
  });
export type RtcSignal = z.infer<typeof RtcSignalSchema>;

export const EMPTY_MEDIA: RtcMedia = { joined: false, audio: false, video: false, screen: false };

export const RTC_LIMITS = {
  /** SDP pode ser grande, mas não infinito */
  maxSdpLength: 200_000,
  /** acima disso, a malha P2P fica pesada: hora de pensar em SFU */
  maxMeshPeers: 6,
} as const;

/**
 * Servidores STUN padrão.
 *
 * STUN basta para a maioria das redes domésticas. Em rede corporativa/NAT
 * simétrico é preciso um TURN (coturn). Em rede local, nem isso: os candidatos
 * de host resolvem.
 */
export const DEFAULT_ICE_SERVERS: readonly { urls: string[] }[] = [
  { urls: ['stun:stun.l.google.com:19302'] },
];

/** Quem inicia a oferta para um par: o menor id, de forma determinística. */
export function shouldInitiateOffer(selfId: string, peerId: string): boolean {
  if (selfId === peerId) return false;
  return selfId < peerId;
}

/** Todos os outros participantes da sala, em ordem estável. */
export function peersFor(roomUserIds: readonly string[], selfId: string): string[] {
  return roomUserIds.filter((id) => id !== selfId).sort();
}

